import React, { useEffect, useRef } from "react";

// Loads the Google Translate widget once and renders a small trigger
// tucked into the top-right corner. The widget translates the entire
// rendered DOM (Adrian's stories, poems, UI) into any supported language.
//
// We keep Google's rendered gadget in the DOM (it's required for the
// translation logic and click-popup) but hide it visually with CSS, then
// wire a native click on our own pill that programmatically clicks
// Google's internal <a> to open its language popup.
export default function LanguageSwitcher() {
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (scriptLoadedRef.current) return;
    if (document.getElementById("google-translate-script")) {
      scriptLoadedRef.current = true;
      return;
    }

    window.googleTranslateElementInit = () => {
      const TE = window.google.translate.TranslateElement;
      // eslint-disable-next-line no-new
      new TE(
        {
          pageLanguage: "en",
          includedLanguages: [
            "af", "ar", "bn", "bs", "ceb", "da", "de", "el", "en",
            "es", "fi", "fr", "he", "hi", "hr", "id", "it", "ja",
            "ko", "mk", "ne", "nl", "pa", "pl", "pt", "ru", "sv",
            "th", "tr", "uk", "vi", "zh-CN",
          ].join(","),
          layout: TE.InlineLayout.SIMPLE,
          autoDisplay: false,
        },
        "google_translate_element"
      );
    };

    const s = document.createElement("script");
    s.id = "google-translate-script";
    s.src =
      "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    s.async = true;
    document.body.appendChild(s);
    scriptLoadedRef.current = true;
  }, []);

  const openLanguagePopup = () => {
    // Google's rendered popup opener is the anchor inside the widget.
    const link = document.querySelector(
      "#google_translate_element .goog-te-gadget-simple a"
    );
    if (link && typeof link.click === "function") {
      link.click();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openLanguagePopup();
    }
  };

  return (
    <div
      className="lang-switcher"
      data-testid="language-switcher"
      role="button"
      tabIndex={0}
      aria-label="Translate page"
      onClick={openLanguagePopup}
      onKeyDown={onKeyDown}
    >
      <span aria-hidden="true" className="lang-switcher-icon">
        🌐
      </span>
      {/* Our own translatable label — Google Translate localises this
          when the page is translated (unlike Google's own widget text
          which is `.skiptranslate` and stays in English). */}
      <span className="lang-switcher-label">Select Language</span>
      <span aria-hidden="true" className="lang-switcher-caret">
        ▾
      </span>
      {/* Google's widget lives here, kept in DOM but visually hidden. */}
      <div id="google_translate_element" className="gt-hidden" />
    </div>
  );
}
